<template>
  <div class="table-pagination">
    <ul class="pagination-pages" :class="{'has-min-width': $props.totalPages > 5}">
      <li class="arrow">
        <a href="#" @click.prevent="goToPage('prev')">
          <i class="icon-sc-chevron-left"></i>
        </a>
      </li>
      <li :class="{'active' : 1 === $props.currentPage}">
        <a href="#" @click.prevent="goToPage(1)">1</a>
      </li>
      <li class="page-placeholder"
          v-if="middlePageNumbers[0] - 1 !== 1 && middlePageNumbers.length">
        <a href="#" @click.prevent>...</a>
      </li>
      <li v-for="page in middlePageNumbers"
          :key="'midpage'+page"
          :class="{'active': page === $props.currentPage}">
        <a href="#" @click.prevent="goToPage(page)">{{page}}</a>
      </li>
      <li class="page-placeholder"
          v-if="middlePageNumbers[middlePageNumbers.length - 1] + 1 !== $props.totalPages && middlePageNumbers.length">
        <a href="#" @click.prevent>...</a>
      </li>
      <li v-if="$props.totalPages > 1" :class="{'active': $props.totalPages === currentPage}">
        <a href="#" @click.prevent="goToPage($props.totalPages)">{{$props.totalPages}}</a>
      </li>
      <li class="arrow">
        <a href="#" @click.prevent="goToPage('next')">
          <i class="icon-sc-chevron-right"></i>
        </a>
      </li>
    </ul>
    <div class="pagination-dropdowns">
      <div class="per-page-dropdown">
        <p class="label">{{ $t('components.tablePagination.showLabel') }}</p>
        <v-select class="custom-dropdown select-dropdown pagination-drop"
                  append-to-body
                  :calculate-position="withPopper"
                  v-model="rowsPerPage"
                  :options="pageOptions"
                  :filterable="false"
                  :searchable="false"
                  @option:selected="emitRowsPerPage"></v-select>
      </div>
      <div class="select-page-dropdown">
        <p class="label">{{ $t('components.tablePagination.goToPageLabel') }}</p>
        <v-select class="custom-dropdown select-dropdown pagination-drop"
                  append-to-body
                  :calculate-position="withPopper"
                  ref=""
                  v-model="intCurrentPage"
                  :options="pagesArray"
                  :filterable="false"
                  :searchable="false"
                  @option:selected="emitPageJump"></v-select>
      </div>
    </div>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core'
export default {
  name: 'table-pagination',
  data () {
    return {
      rowsPerPage: 5,
      intCurrentPage: 1,
      placement: 'bottom'
    }
  },
  props: {
    totalPages: {
      type: Number,
      default: 1
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pageOptions: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  computed: {
    middlePageNumbers () {
      const middlePages = []
      const totalPages = this.$props.totalPages

      // handle edge cases
      if (totalPages === 4) return [2, 3]
      if (totalPages === 5) return [2, 3, 4]

      // handle rest
      for (let i = 2; i < totalPages; i++) {
        middlePages.push(i)
      }
      if (this.$props.currentPage === 1 && totalPages > 1) {
        return middlePages.slice(this.$props.currentPage - 1, this.$props.currentPage)
      }
      if (this.$props.currentPage === this.$props.totalPages) {
        return middlePages.slice(middlePages.length - 1, middlePages.length)
      }
      return middlePages.slice(this.$props.currentPage - 2, this.$props.currentPage - 1)
    },
    pagesArray () {
      const pageArr = []
      for (let i = 1; i <= this.$props.totalPages; i++) {
        pageArr.push(i)
      }

      return pageArr.length ? pageArr : [1]
    }
  },
  methods: {
    goToPage (page) {
      this.$emit('page-change', page)
    },
    emitRowsPerPage () {
      this.$emit('rows-change', this.rowsPerPage)
    },
    emitPageJump () {
      this.$emit('page-jump', this.intCurrentPage)
    },
    resetInternalPageNumber () {
      this.intCurrentPage = 1
    },
    withPopper (dropdownList, component, { width }) {
      dropdownList.style.width = width

      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -1]
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn ({ state }) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            }
          }]
      })
      return () => popper.destroy()
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.rowsPerPage = this.$props.pageOptions[0]
    })
  }
}
</script>

<style scoped lang="scss">
.table-pagination {
  display: flex;
  flex-direction: column;

  @media (map_get($breakpoints, sm)) {
    flex-direction: row;
    //align-items: center;
  }

  .pagination-pages {
    display: flex;
    justify-content: space-between;
    padding: 0.8rem;
    background: $white;
    border-radius: 5px;
    box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.08);

    @media (map_get($breakpoints, sm)) {
      justify-content: flex-start;

      &.has-min-width {
        min-width: 240px;
        justify-content: space-between;
      }
    }

    li {
      min-width: 28px;
      min-height: 28px;
      margin: 0 0.2rem;
      background: $white;
      border-radius: 5px;
      transition: background-color 0.2s;

      &:hover {
        background-color: $grey;
      }

      a {
        display: block;
        line-height: 28px;
        text-align: center;
        text-decoration: none;
        font-size: 14px;

        i {
          line-height: 28px;
        }
      }

      &:not(.arrow) a {
        color: $dark-grey;
        font-size: 12px;
      }

      &.active {
        background-color: $primary;

        a {
          color: $white;
        }

        &:hover {
          background-color: $primary;
        }
      }

      &.page-placeholder {
        &:hover {
          background-color: $white;

          a {
            cursor: default;
          }
        }
      }

      &.arrow:hover {
        background-color: $white;
      }
    }
  }

  .pagination-dropdowns {
    display: flex;
    justify-content: space-between;
    margin-top: 1.6rem;

    @media (map_get($breakpoints, sm)) {
      justify-content: flex-start;
      margin-top: 0;
    }

    .per-page-dropdown, .select-page-dropdown {
      display: flex;
      align-items: center;
    }

    .label {
      font-size: 1.4rem;
      margin-right: 0.8rem;
      margin-bottom: 2px;
      color: $text;

      @media (map_get($breakpoints, sm)) {
        margin-left: 4rem;
      }
    }

    .custom-dropdown {
      min-width: 65px;
      width: 65px;

      .vs__selected {
        padding: 1rem !important;
      }
    }
  }
}
</style>
